import { Box, Button, Divider, Stack, Typography } from '@mui/material'
import { CandidateRefereeSurveyRequest } from '../../../firebase/types'
import styles from '../index.module.scss'
import sparkLogo from '../../../assets/images/share/sparkIcon_hr.png'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import {
    AUS_STATES,
    locationStateToRegMap,
    ReferenceSurveySteps_v3,
} from '../helper'
import { UserRegSx } from '../../../components/TextField'
import { FormRadioGroup } from '../../../components/FormComponents/FormRadioGroup'
import {
    defaultCheckboxIcon,
    GreenCheckedIcon,
    YellowCheckedIcon,
} from '../../../components/Checkbox'
import FormInputText_v2 from '../../../components/FormComponents/FormInputText_v2'
import FormSelect_v2 from '../../../components/FormComponents/FormSelect_v2'
import { GENERAL_API } from '../../../firebase/general-apis'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setErrorMsg } from '../../../redux/uiSlice'
import { captureException } from '@sentry/react'
import { logger } from '../../../helpers/logger'
import { getError } from '../../../helpers/utils'
import { logEvent } from 'firebase/analytics'
import { analytics } from '../../../firebase/configs'
import {
    RefereeSurveyErrorReason,
    getRefereeSurveyErrorState,
} from '../v3.helper'

const answerSchema = z.enum(['yes', 'no', 'unsure'] as const)
const requiredFieldMessage = 'Incomplete*'
export const nonEmptyStringSchema = z
    .string(requiredFieldMessage)
    .trim()
    .min(1, requiredFieldMessage)
    .default('')
    .refine((v) => v.trim() !== '', requiredFieldMessage)

const answerOptions = [
    {
        label: 'Yes',
        value: 'yes',
        radioProps: {
            checkedIcon: YellowCheckedIcon,
            icon: defaultCheckboxIcon,
        },
    },
    {
        label: 'No',
        value: 'no',
        radioProps: {
            checkedIcon: GreenCheckedIcon,
            icon: defaultCheckboxIcon,
        },
    },
]
const answerOptionsWithUnsure = [
    ...answerOptions,
    {
        label: 'Unsure',
        value: 'unsure',
        radioProps: {
            checkedIcon: YellowCheckedIcon,
            icon: defaultCheckboxIcon,
        },
    },
]

const formSchema = z.object({
    refereeDetails: z.object({
        fullName: nonEmptyStringSchema,
        locationState: z.enum(AUS_STATES, requiredFieldMessage),
        registrationType: nonEmptyStringSchema,
        registrationNumber: nonEmptyStringSchema,
    }),

    candidateDetails: z.object({
        fullName: nonEmptyStringSchema,
        relationship: nonEmptyStringSchema,

        workedWithAt: nonEmptyStringSchema,

        workedWithLength: z.enum(
            [
                '0 - 6 months',
                '6 months - 1 year',
                '1 - 2 years',
                '2 years +',
            ] as const,
            requiredFieldMessage
        ),
    }),
    directWorkWithChildren: z.object({
        answer: answerSchema.extract(['yes', 'no'], requiredFieldMessage),
        comment: z.string().nullable().default(null),
    }),
    concerns: z.object({
        hasChildConductConcerns: z.discriminatedUnion(
            'answer',
            [
                z.object({
                    answer: answerSchema.extract(['yes'], requiredFieldMessage),
                    concernDescription: nonEmptyStringSchema,
                    wasConcernInvestigatedOrSubstantiated: nonEmptyStringSchema,
                    actionTaken: nonEmptyStringSchema,
                }),
                z.object({
                    answer: answerSchema.exclude(['yes']),
                }),
            ],
            requiredFieldMessage
        ),
        hasDisciplinaryActionTowardsChild: z.discriminatedUnion(
            'answer',
            [
                z.object({
                    answer: answerSchema.extract(['yes']),
                    concernDescription: nonEmptyStringSchema,
                }),
                z.object({
                    answer: answerSchema.exclude(['yes']),
                }),
            ],
            requiredFieldMessage
        ),
        hasConcernsWorkingWithChildren: z.discriminatedUnion(
            'answer',
            [
                z.object({
                    answer: answerSchema.extract(['yes']),
                    concernDescription: nonEmptyStringSchema,
                }),
                z.object({
                    answer: answerSchema.exclude(['yes']),
                }),
            ],
            requiredFieldMessage
        ),
    }),
    hasIntegrityOrProfessionalConductConcerns: z.discriminatedUnion(
        'answer',
        [
            z.object({
                answer: answerSchema.extract(['yes'], requiredFieldMessage),
                concernDescription: nonEmptyStringSchema,
                wasMatterResolved: nonEmptyStringSchema,
            }),
            z.object({
                answer: answerSchema.exclude(['yes'], requiredFieldMessage),
            }),
        ],
        requiredFieldMessage
    ),
    finalThoughts: z.string().default(''),
})

export type ReferenceResponse_v3 = z.infer<typeof formSchema>

const ReferenceSurvey = ({
    data,
    setStep,
}: {
    data: CandidateRefereeSurveyRequest
    setStep: (e: ReferenceSurveySteps_v3) => void
}) => {
    const { control, handleSubmit, watch, resetField } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            candidateDetails: {
                fullName: data.teacherName,
            },
        },
    })
    const regLocationState = watch('refereeDetails.locationState')
    const regType = watch('refereeDetails.registrationType')

    const hasChildConductConcernsAnswer = watch(
        'concerns.hasChildConductConcerns.answer'
    )
    const hasDisciplinaryActionTowardsChildAnswer = watch(
        'concerns.hasDisciplinaryActionTowardsChild.answer'
    )
    const hasConcernsWorkingWithChildrenAnswer = watch(
        'concerns.hasConcernsWorkingWithChildren.answer'
    )
    const hasIntegrityOrProfessionalConductConcernsAnswer = watch(
        'hasIntegrityOrProfessionalConductConcerns.answer'
    )

    const { refereeId } = useParams()
    const dispatch = useDispatch()

    const handleSubmissionError = (
        reason: RefereeSurveyErrorReason,
        error: unknown,
        eventType: string
    ) => {
        const mappedError = getRefereeSurveyErrorState(reason)
        dispatch(setErrorMsg(mappedError))
        logEvent(analytics, 'referee_survey_submission_failed', {
            refereeId,
            route: '/referee/:refereeId',
            reason,
            online: navigator.onLine,
        })

        captureException(
            error instanceof Error ? error : new Error(eventType),
            {
                tags: {
                    feature: 'Referee Survey',
                    eventType,
                    route: '/referee/:refereeId',
                    refereeId: refereeId ?? 'missing',
                },
                extra: { reason },
            }
        )
    }

    const onSubmit = async (data: ReferenceResponse_v3) => {
        try {
            if (typeof refereeId !== 'string') {
                throw new Error(`Referee ID is missing`)
            }
            await GENERAL_API.referenceSurvey.submit_v3(refereeId, data)
            setStep('end-agree')
        } catch (e) {
            logger.debug(`Error: ${getError(e)}`)
            handleSubmissionError(
                'submission-failed',
                e,
                'referee_survey_submit_error'
            )
        }
    }

    return (
        <Stack className={styles.bgPage} justifyContent={'flex-start'}>
            <Stack className={styles.refereeSurvey}>
                <img src={sparkLogo} alt={'Spark Relief Teachers'} />
                <Stack
                    sx={{
                        width: '100%',
                        px: 0.5,
                        py: 2.5,
                        backgroundColor: styles.secondaryBlue700,
                        borderRadius: '16px',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h4" sx={{ color: styles.shades0 }}>
                        Spark Pre-Employment Reference Survey
                        <br />- {data.teacherName}
                    </Typography>
                </Stack>

                <Stack className={styles.surveyContent_v3}>
                    <Box>
                        <Typography variant="h6">Your Details</Typography>
                    </Box>
                    <FormInputText_v2
                        control={control}
                        name="refereeDetails.fullName"
                        formLabel={'Full Name'}
                        textFieldProps={{
                            sx: UserRegSx,
                            placeholder: 'e.g. Jane Doe',
                        }}
                    />

                    <FormSelect_v2
                        control={control}
                        name="refereeDetails.locationState"
                        options={formSchema.shape.refereeDetails.shape.locationState.options.map(
                            (el) => ({ label: el, value: el })
                        )}
                        selectProps={{
                            label: 'State',
                        }}
                        controlProps={{
                            sx: UserRegSx,
                        }}
                        formLabel={'Location'}
                        onChange={() => {
                            resetField('refereeDetails.registrationType')
                        }}
                    />

                    {!!regLocationState ? (
                        <FormSelect_v2
                            control={control}
                            name="refereeDetails.registrationType"
                            options={locationStateToRegMap[regLocationState]}
                            controlProps={{
                                sx: UserRegSx,
                            }}
                            placeholder={'Select the option you currently hold'}
                            formLabel={'Current Registration'}
                        />
                    ) : null}

                    {!!regType ? (
                        <>
                            <FormInputText_v2
                                control={control}
                                name={'refereeDetails.registrationNumber'}
                                formLabel={`${regType.toLocaleUpperCase()} Number`}
                                textFieldProps={{
                                    sx: UserRegSx,
                                }}
                            />
                        </>
                    ) : null}
                </Stack>

                <Stack className={styles.surveyContent_v3}>
                    <Box>
                        <Typography variant="h6">Candidate Details</Typography>
                    </Box>
                    <FormInputText_v2
                        control={control}
                        name={'candidateDetails.fullName'}
                        formLabel={"Candidate's Full Name"}
                        textFieldProps={{
                            sx: UserRegSx,
                        }}
                    />
                    <FormInputText_v2
                        control={control}
                        name={'candidateDetails.relationship'}
                        formLabel={
                            'What is your working relationship with the candidate?'
                        }
                        textFieldProps={{
                            sx: UserRegSx,
                            placeholder: "e.g. I was John's supervisor...",
                        }}
                    />
                    <FormInputText_v2
                        control={control}
                        name={'candidateDetails.workedWithAt'}
                        formLabel={
                            'Organisation/school/centre where you worked with the applicant:'
                        }
                        textFieldProps={{
                            sx: UserRegSx,
                            placeholder: 'e.g.  Spark Primary School',
                        }}
                    />

                    <FormSelect_v2
                        control={control}
                        name={'candidateDetails.workedWithLength'}
                        options={formSchema.shape.candidateDetails.shape.workedWithLength.options.map(
                            (el) => ({ label: el, value: el })
                        )}
                        controlProps={{
                            sx: UserRegSx,
                        }}
                        placeholder="Select"
                        formLabel="How long did you work with the candidate?"
                    />
                </Stack>

                <Stack className={styles.surveyContent_v3}>
                    <Box>
                        <Typography variant="h6">
                            Direct work with children
                        </Typography>
                    </Box>

                    <FormRadioGroup
                        control={control}
                        name={'directWorkWithChildren.answer'}
                        options={[
                            {
                                label: 'Yes',
                                value: 'yes',
                                radioProps: {
                                    checkedIcon: GreenCheckedIcon,
                                    icon: defaultCheckboxIcon,
                                },
                            },
                            {
                                label: 'No',
                                value: 'no',
                                radioProps: {
                                    checkedIcon: YellowCheckedIcon,
                                    icon: defaultCheckboxIcon,
                                },
                            },
                        ]}
                        radioGroupProps={{
                            row: true,
                            sx: { gap: 3 },
                        }}
                        formControlProps={{
                            sx: UserRegSx,
                        }}
                        formLabel={
                            'Have you directly observed the candidate working with children?'
                        }
                    />

                    <FormInputText_v2
                        control={control}
                        name={'directWorkWithChildren.comment'}
                        textFieldProps={{
                            sx: UserRegSx,
                        }}
                        formLabel={'Optional Comment'}
                    />
                </Stack>

                <Stack className={styles.surveyContent_v3}>
                    <Box>
                        <Typography variant="h6">
                            Concerns about behaviour with children
                        </Typography>
                    </Box>

                    <FormRadioGroup
                        control={control}
                        name={'concerns.hasChildConductConcerns.answer'}
                        options={answerOptionsWithUnsure}
                        radioGroupProps={{
                            row: true,
                            sx: { gap: 3 },
                        }}
                        formControlProps={{
                            sx: UserRegSx,
                        }}
                        formLabel={`
                            Were any concerns, complaints, allegations, or
                            disclosures raised about the candidate's behaviour
                            or conduct with children?
                        `}
                    />

                    {hasChildConductConcernsAnswer === 'yes' ? (
                        <Stack sx={{ gap: 3 }}>
                            <Typography>
                                If yes, please briefly explain:
                            </Typography>

                            <FormInputText_v2
                                control={control}
                                name={
                                    'concerns.hasChildConductConcerns.concernDescription'
                                }
                                textFieldProps={{
                                    sx: UserRegSx,
                                }}
                                formLabel={`What was the concern?`}
                            />
                            <FormInputText_v2
                                control={control}
                                name={
                                    'concerns.hasChildConductConcerns.wasConcernInvestigatedOrSubstantiated'
                                }
                                textFieldProps={{
                                    sx: UserRegSx,
                                }}
                                formLabel={`Was the concern investigated or
                                    substantiated?`}
                            />

                            <FormInputText_v2
                                control={control}
                                name={
                                    'concerns.hasChildConductConcerns.actionTaken'
                                }
                                textFieldProps={{
                                    sx: UserRegSx,
                                }}
                                formLabel={`What action was taken?`}
                            />
                        </Stack>
                    ) : null}

                    <Divider flexItem />

                    <FormRadioGroup
                        control={control}
                        name={
                            'concerns.hasDisciplinaryActionTowardsChild.answer'
                        }
                        options={answerOptionsWithUnsure}
                        radioGroupProps={{
                            row: true,
                            sx: { gap: 3 },
                        }}
                        formControlProps={{
                            sx: UserRegSx,
                        }}
                        formLabel={`
                                Was any disciplinary action taken against the
                                candidate for inappropriate or unprofessional
                                conduct towards a child?
                            `}
                    />

                    {hasDisciplinaryActionTowardsChildAnswer === 'yes' ? (
                        <FormInputText_v2
                            control={control}
                            name={
                                'concerns.hasDisciplinaryActionTowardsChild.concernDescription'
                            }
                            textFieldProps={{
                                sx: UserRegSx,
                            }}
                            formLabel={`If yes, please briefly explain:`}
                        />
                    ) : null}

                    <FormRadioGroup
                        control={control}
                        name={'concerns.hasConcernsWorkingWithChildren.answer'}
                        options={answerOptions}
                        radioGroupProps={{
                            row: true,
                            sx: { gap: 3 },
                        }}
                        formControlProps={{
                            sx: UserRegSx,
                        }}
                        formLabel={
                            'Do you have any concerns about the candidate working directly with children?'
                        }
                    />
                    {hasConcernsWorkingWithChildrenAnswer === 'yes' ? (
                        <FormInputText_v2
                            control={control}
                            name={
                                'concerns.hasConcernsWorkingWithChildren.concernDescription'
                            }
                            textFieldProps={{
                                sx: UserRegSx,
                            }}
                            formLabel={'If yes, please briefly explain:'}
                        />
                    ) : null}
                </Stack>

                <Stack className={styles.surveyContent_v3}>
                    <Box>
                        <Typography variant="h6">
                            Integrity and professional conduct
                        </Typography>
                    </Box>
                    <FormRadioGroup
                        control={control}
                        name={
                            'hasIntegrityOrProfessionalConductConcerns.answer'
                        }
                        options={answerOptions}
                        radioGroupProps={{
                            row: true,
                            sx: { gap: 3 },
                        }}
                        formControlProps={{
                            sx: UserRegSx,
                        }}
                        formLabel={
                            <>
                                Did you have any concerns about the candidate's
                                integrity or professional conduct?
                                <br />
                                <br />
                                Examples may include unethical behaviour,
                                conflicts of interest, or mishandling sensitive
                                or confidential information.
                            </>
                        }
                    />

                    {hasIntegrityOrProfessionalConductConcernsAnswer ===
                    'yes' ? (
                        <Stack sx={{ gap: 3 }}>
                            <Typography>
                                If yes, please briefly explain:
                            </Typography>
                            <FormInputText_v2
                                control={control}
                                name={
                                    'hasIntegrityOrProfessionalConductConcerns.concernDescription'
                                }
                                textFieldProps={{
                                    sx: UserRegSx,
                                }}
                                formLabel={`What was the concern?`}
                            />
                            <FormInputText_v2
                                control={control}
                                name={
                                    'hasIntegrityOrProfessionalConductConcerns.wasMatterResolved'
                                }
                                textFieldProps={{
                                    sx: UserRegSx,
                                }}
                                formLabel={`Was the matter resolved?`}
                            />
                        </Stack>
                    ) : null}
                </Stack>

                <Stack className={styles.surveyContent_v3}>
                    <Box>
                        <Typography variant="h6">Final thoughts</Typography>
                    </Box>

                    <FormInputText_v2
                        control={control}
                        name="finalThoughts"
                        textFieldProps={{
                            sx: UserRegSx,
                        }}
                        formLabel={
                            <>
                                Is there anything else we should know about the
                                candidate's suitability to work with children
                            </>
                        }
                    />
                </Stack>

                <Button
                    variant="contained"
                    color="primaryBlue500"
                    name="referenceSubmit"
                    onClick={handleSubmit(onSubmit)}
                    sx={{
                        width: 400,
                        maxWidth: '100%',
                        px: 2,
                        py: 1.5,
                        borderRadius: '40px',
                    }}
                >
                    Submit
                </Button>
            </Stack>
        </Stack>
    )
}

export default ReferenceSurvey
